import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Search, ChevronLeft, ChevronRight, Edit, Plus } from 'lucide-react'
import toast from 'react-hot-toast'

import { PageHeader } from '@/components/shared/PageHeader'
import { EmptyState } from '@/components/shared/EmptyState'
import { ListLoader } from '@/components/shared/ListLoader'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { useAuthStore } from '@/store/auth.store'
import { roleService, type RoleListItem } from '@/services/roleService'

export const RolesList: React.FC = () => {
  const { t, i18n } = useTranslation('common')
  const locale = i18n.language?.toLowerCase().startsWith('ar') ? 'ar-SA' : 'en-US'
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId ?? 0

  const [rows, setRows] = useState<RoleListItem[]>([])
  const [loading, setLoading] = useState(false)
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [totalRecords, setTotalRecords] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [searchQuery, setSearchQuery] = useState('')

  function formatDate(dateStr?: string | null): string {
    if (!dateStr) return '—'
    try {
      return new Date(dateStr).toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' })
    } catch {
      return String(dateStr)
    }
  }

  useEffect(() => {
    if (!branchId) return

    const load = async () => {
      try {
        setLoading(true)
        const res = await roleService.getAllRoles(branchId, pageNumber, pageSize)
        setRows(res.data || [])
        setTotalRecords(res.totalRecords || 0)
        const tp = res.totalRecords ? Math.ceil(res.totalRecords / res.pageSize) : 0
        setTotalPages(tp)
      } catch (error: any) {
        const msg = error?.response?.data?.message || t('rolePage.fetchFailed')
        toast.error(msg)
        setRows([])
        setTotalRecords(0)
        setTotalPages(0)
      } finally {
        setLoading(false)
      }
    }

    load()
  }, [branchId, pageNumber, pageSize])

  const filteredRows = useMemo(() => {
    if (!searchQuery.trim()) return rows
    const q = searchQuery.toLowerCase()
    const activeLabel = t('rolePage.statusActive').toLowerCase()
    const inactiveLabel = t('rolePage.statusInactive').toLowerCase()
    return rows.filter((r) => {
      const status = r.isActive === 1 ? activeLabel : inactiveLabel
      return `${r.id} ${r.roleName} ${r.description} ${status} ${r.createdDate ?? ''}`.toLowerCase().includes(q)
    })
  }, [rows, searchQuery, t])

  const handlePageSizeChange = (value: string) => {
    setPageSize(Number(value))
    setPageNumber(1)
  }

  const pagFrom = totalRecords === 0 ? 0 : (pageNumber - 1) * pageSize + 1
  const pagTo = totalRecords === 0 ? 0 : Math.min(pageNumber * pageSize, totalRecords)
  const pageTotal = Math.max(1, totalPages || 1)

  if (!branchId) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader title={t('rolePage.pageTitle')} description={t('rolePage.pageDescription')} />
        <Card>
          <CardContent className="p-6">
            <EmptyState title={t('rolePage.branchRequiredTitle')} description={t('rolePage.branchRequiredDescriptionList')} />
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title={t('rolePage.pageTitle')}
        description={t('rolePage.pageDescription')}
        action={{ label: t('rolePage.addRole'), onClick: () => navigate('/security/roles/new'), icon: Plus }}
      />

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4 justify-between">
            <div className="relative w-full sm:w-72">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder={t('rolePage.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <ListLoader />
          ) : filteredRows.length === 0 ? (
            <div className="p-12">
              <EmptyState
                title={t('rolePage.emptyNoRoles')}
                description={searchQuery ? t('rolePage.emptySearchHint') : t('rolePage.emptyNoCreated')}
              />
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow className="border-b-2 border-primary/20 hover:bg-transparent">
                    <TableHead className="h-12 px-4 text-left bg-primary/5 font-bold text-primary">{t('rolePage.colId')}</TableHead>
                    <TableHead className="h-12 px-4 text-left bg-primary/5 font-bold text-primary">{t('rolePage.colRoleName')}</TableHead>
                    <TableHead className="h-12 px-4 text-left bg-primary/5 font-bold text-primary">{t('rolePage.colDescription')}</TableHead>
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary">{t('rolePage.colStatus')}</TableHead>
                    <TableHead className="h-12 px-4 text-left bg-primary/5 font-bold text-primary">{t('rolePage.colCreatedDate')}</TableHead>
                    <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary w-24">{t('rolePage.colAction')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredRows.map((row) => (
                    <TableRow key={row.id} className="border-b hover:bg-primary/5">
                      <TableCell className="px-4 py-2 align-middle">{row.id}</TableCell>
                      <TableCell className="px-4 py-2 align-middle">{row.roleName}</TableCell>
                      <TableCell className="px-4 py-2 align-middle">{row.description || '—'}</TableCell>
                      <TableCell className="px-4 py-2 text-center align-middle">
                        <Badge variant={row.isActive === 1 ? 'default' : 'secondary'}>
                          {row.isActive === 1 ? t('rolePage.statusActive') : t('rolePage.statusInactive')}
                        </Badge>
                      </TableCell>
                      <TableCell className="px-4 py-2 align-middle">{formatDate(row.createdDate)}</TableCell>
                      <TableCell className="px-4 py-2 text-center align-middle">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-primary hover:bg-primary/10"
                          title={t('rolePage.editTitle')}
                          onClick={() => navigate(`/security/roles/${row.id}`)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <div className="border-t px-6 py-4 flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span>{t('rolePage.pageSize')}</span>
                  <select
                    value={pageSize}
                    onChange={(e) => handlePageSizeChange(e.target.value)}
                    className="h-8 rounded-md border bg-background px-2 text-sm"
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                </div>
                <div className="text-sm text-muted-foreground">
                  {t('rolePage.paginationShowing', { from: pagFrom, to: pagTo, total: totalRecords })}
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" disabled={pageNumber <= 1} onClick={() => setPageNumber((p) => p - 1)}>
                    <ChevronLeft className="h-4 w-4" />
                    {t('rolePage.previous')}
                  </Button>
                  <span className="text-sm">
                    {t('rolePage.pageOf', { page: pageNumber, totalPages: pageTotal })}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={pageNumber >= totalPages || totalPages === 0}
                    onClick={() => setPageNumber((p) => p + 1)}
                  >
                    {t('rolePage.next')}
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
